import React, { FC, memo, useEffect, useState } from 'react';
import {
    Box,
    TextField,
    MenuItem,
    Button,
    Grid,
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import CloseIcon from '@mui/icons-material/Close';
import { useUserContext } from '../hook/useUserContext';
import { Modal } from '../../../components/Modal';
import User from '../../../security/type/User';
import UserRole from '../../../security/type/enum/UserRole';

type EditModalProps = {
    open: boolean;
    onClose: () => void;
};

const UserEditModal: FC<EditModalProps> = ({ open, onClose }) => {
    const { userToEdit, updateSelectedUser } = useUserContext();

    const [formData, setFormData] = useState<Partial<User>>({
        firstName: '',
        lastName: '',
        mail: '',
        phoneNumber: '',
        role: UserRole.ROLE_CLIENT,
    });

    const roleOptions = Object.values(UserRole).map((role) => ({
        value: role,
        label: role.replace('ROLE_', '').replace('_', ' '),
    }));

    useEffect(() => {
        if (userToEdit && open) {
            setFormData({
                firstName: userToEdit.firstName ?? '',
                lastName: userToEdit.lastName ?? '',
                mail: userToEdit.mail ?? '',
                phoneNumber: userToEdit.phoneNumber ?? '',
                role: userToEdit.role ?? UserRole.ROLE_CLIENT,
            });
        }
    }, [userToEdit, open]);

    const handleChange = (field: keyof User) => (
        event: React.ChangeEvent<HTMLInputElement>
    ) => {
        setFormData((prev) => ({
            ...prev,
            [field]: event.target.value,
        }));
    };

    const handleSubmit = async (): Promise<void> => {
        try {
            const updatedUserData: User = {
                ...userToEdit,
                ...formData,
            } as User;

            await updateSelectedUser(userToEdit.id, updatedUserData);
            onClose();
        } catch (error) {
            console.error('Failed to update user data', error);
        }
    };

    return (
        <Modal
            open={open}
            onClose={onClose}
            title="Edit User"
            maxWidth="md"
            fullWidth={true}
            actions={
                <>
                    <Button
                        startIcon={<CloseIcon />}
                        onClick={onClose}
                        sx={{ textTransform: 'none' }}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        color="primary"
                        startIcon={<SaveIcon />}
                        onClick={handleSubmit}
                        sx={{ textTransform: 'none', fontWeight: 600 }}
                    >
                        Save Changes
                    </Button>
                </>
            }
        >
            <Box
                component="form"
                onSubmit={(e) => {
                    e.preventDefault();
                    handleSubmit();
                }}
            >
                <Grid container spacing={3}>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            fullWidth
                            label="First Name"
                            name="firstName"
                            value={formData.firstName}
                            onChange={handleChange('firstName')}
                            placeholder="Enter first name"
                            variant="outlined"
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            fullWidth
                            label="Last Name"
                            name="lastName"
                            value={formData.lastName}
                            onChange={handleChange('lastName')}
                            placeholder="Enter last name"
                            variant="outlined"
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            fullWidth
                            label="Email"
                            name="mail"
                            type="email"
                            value={formData.mail}
                            onChange={handleChange('mail')}
                            placeholder="Enter email"
                            variant="outlined"
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            fullWidth
                            label="Phone Number"
                            name="phoneNumber"
                            value={formData.phoneNumber}
                            onChange={handleChange('phoneNumber')}
                            placeholder="Enter phone number"
                            variant="outlined"
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            select
                            label="Role"
                            name="role"
                            value={formData.role}
                            onChange={handleChange('role')}
                            variant="outlined"
                        >
                            {roleOptions.map((option) => (
                                <MenuItem key={option.value} value={option.value}>
                                    {option.label}
                                </MenuItem>
                            ))}
                        </TextField>
                    </Grid>
                </Grid>
            </Box>
        </Modal>
    );
};

export default memo(UserEditModal);
